import type { Editor } from '@tiptap/react';
import type { UIMessage } from 'ai';
import { hashClassificationSource } from '@flintmint/core-shared/medical-note';
import type { ClassifierResult } from '@flintmint/core-shared/medical-note';
import { stripHtml } from '@flintmint/core-shared/utils';
import type { Note } from '@flintmint/core-shared/rxdb';

import { getLatestCurrentTurnMedicalClassification } from '../current-turn-assistant-messages';
import { isNoteDocument, type PersistedDocument } from '../document-utils';
import { getRequestEditor } from '../request-editor';
import { getEditorHTML } from '@/utils/editor-utils';
import type { CompletionOutcome } from './agent-run-model';
import type { PersistedDocumentPatch } from '../review/review-session-types';

type CompletionDocumentPatch = PersistedDocumentPatch & {
  classificationCache?: Note['classificationCache'];
};

export function buildCompletionDocumentPatch({
  requestId,
  document,
  editor,
  messages,
  outcome,
}: {
  requestId: string;
  document?: PersistedDocument;
  editor?: Editor | null;
  messages?: UIMessage[];
  outcome: CompletionOutcome;
}): CompletionDocumentPatch | null {
  if (!document) return null;

  const patch: CompletionDocumentPatch = {};
  const shouldPersistCompletionClassification = outcome === 'completed' || outcome === 'direct_write';

  if (outcome === 'direct_write') {
    const resolvedEditor = getRequestEditor(requestId, editor);
    if (!resolvedEditor) {
      return null;
    }

    patch.content = getEditorHTML(resolvedEditor);
  }

  if (messages && shouldPersistCompletionClassification && isNoteDocument(document)) {
    const resolvedEditor = getRequestEditor(requestId, editor);
    const classificationCachePatch = buildCompletionClassificationCachePatch({
      messages,
      editor: resolvedEditor,
      requestId,
      document,
      contentChanged: outcome === 'direct_write',
    });
    Object.assign(patch, classificationCachePatch);
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

function buildCompletionClassificationCachePatch({
  messages,
  editor,
  requestId,
  document,
  contentChanged,
}: {
  messages: UIMessage[];
  editor: Editor | null;
  requestId: string;
  document: PersistedDocument | undefined;
  contentChanged: boolean;
}): Pick<CompletionDocumentPatch, 'classificationCache'> {
  if (!document || !isNoteDocument(document)) return {};

  const classification = getLatestCurrentTurnMedicalClassification(messages);
  if (!classification) return {};

  const contentHash = contentChanged
    ? getCompletionEditorContentHash(requestId, editor)
    : getPersistedParsedContentHash(document);
  if (!contentHash) return {};

  return buildClassificationCachePatch({ classification, contentHash }) ?? {};
}

export function buildClassificationCachePatch({
  classification,
  contentHash,
}: {
  classification: ClassifierResult | null;
  contentHash: string | null;
}): Pick<Note, 'classificationCache'> | null {
  if (!classification || !contentHash) return null;

  return {
    classificationCache: {
      contentHash,
      classification,
    },
  };
}

function getCompletionEditorContentHash(requestId: string, editor?: Editor | null): string | null {
  const requestEditor = getRequestEditor(requestId, editor);
  const parsedContent =
    requestEditor && typeof requestEditor.getHTML === 'function' ? stripHtml(getEditorHTML(requestEditor)) : null;

  return parsedContent?.trim() ? hashClassificationSource(parsedContent) : null;
}

export function getPersistedParsedContentHash(document: PersistedDocument | undefined): string | null {
  if (!document || !isNoteDocument(document)) return null;

  const parsedContent = document.parsedContent;
  return parsedContent?.trim() ? hashClassificationSource(parsedContent) : null;
}
